import React, { useState } from "react";
import SkeyechartUsersTable from "./SkeyechartUsersTable";
import { AddUserDialog, EditUserDialog } from "./SkeyechartUsersDialogs";
import {
  fetchUsers,
  addUser,
  updateUser,
  deleteUser,
} from "./SkeyechartUsersAPI";
import PageLayout from "../../../components/PageLayout";
import SearchToolbar from "../../../components/SearchToolbar";
import usePageTitle from "../../../services/usePageTitle";
import usePageState from "../../../hooks/usePageState";
import useIsRoleUser from "../../../hooks/useIsRoleUser";
import { extractMsg } from "../../../utils/errorUtils";
import skeyechartsUsersHelp from "../../../content/help/skeyecharts-users.json";

export default function SkeyechartsUsers({ pageTitle = "Skeyechart Users" }) {
  usePageTitle("SkeyeCharts Users - SkeyeGate™");

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchUsers,
    filterFields: [
      "username",
      "first_name",
      "last_name",
      "email",
      (u) => (Array.isArray(u.roles) ? u.roles.join(" ") : ""),
    ],
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const isRoleUser = useIsRoleUser();

  const handleAddUser = async (values) => {
    try {
      await addUser(values);
      messageApi.success("User successfully added.");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleEditUser = async (values) => {
    try {
      await updateUser({ userId: userToEdit.id, ...values });
      messageApi.success("User successfully updated.");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      messageApi.success("User deleted successfully.");
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to delete user"),
        key: "delete-user-error",
      });
    }
  };

  return (
    <div className="skeyecharts-users-page">
      {contextHolder}
      <PageLayout
        title={pageTitle}
        logo="/img/majesteye-icon-skeye-charts-square-white-background-no-label"
        description="Manager for Skeyecharts users, allowing you to create, edit, delete, and organize your user accounts in one place."
        helpContent={skeyechartsUsersHelp}
      >
        <SearchToolbar
          search={searchText}
          onSearchChange={handleSearch}
          onClearFilter={clearFilter}
          addButtonLabel="Add User"
          onAdd={() => setAddModalOpen(true)}
          showAdd={!isRoleUser}
        />

        <SkeyechartUsersTable
          data={filteredData}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onEditUser={(user) => {
            setUserToEdit(user);
            setEditModalOpen(true);
          }}
          onDeleteUser={handleDeleteUser}
          isRoleUser={isRoleUser}
        />
      </PageLayout>

      <AddUserDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUser}
      />
      <EditUserDialog
        open={editModalOpen}
        user={userToEdit}
        onClose={() => setEditModalOpen(false)}
        onSave={handleEditUser}
      />
    </div>
  );
}
